/* istanbul ignore file */
/**
 * Lightweight structured console logger + global error listener.
 *
 * Replaces `@amzn/katal-logger`, which was configured console-only (`url: ""`,
 * `logToConsole: true`) — so this preserves the same behavior with no external
 * dependency: it logs structured lines to the browser console and captures
 * uncaught errors and unhandled promise rejections at app startup.
 *
 * Implemented with plain functions + an object literal (no class) so it works
 * with the babel `preset-typescript` build, which does not transform TypeScript
 * constructor parameter properties.
 */
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const CONTEXT: Record<string, unknown> = {
    site: 'BAC-Snapshot',
    service: 'BAC-Snapshot-Website',
};

function emit(level: LogLevel, message: string, extra?: unknown): void {
    const line = { level, message, ...CONTEXT, ...(extra !== undefined ? { extra } : {}) };
    // eslint-disable-next-line no-console
    (console[level] ?? console.log)(JSON.stringify(line));
}

export const logger = {
    debug: (message: string, extra?: unknown): void => emit('debug', message, extra),
    info: (message: string, extra?: unknown): void => emit('info', message, extra),
    warn: (message: string, extra?: unknown): void => emit('warn', message, extra),
    error: (message: string, extra?: unknown): void => emit('error', message, extra),

    /** Attach global handlers so uncaught errors / rejections are logged. */
    addErrorListener(): void {
        if (typeof window === 'undefined') return;
        window.addEventListener('error', (e: ErrorEvent) => {
            emit('error', 'Uncaught error', {
                message: e.message,
                filename: e.filename,
                lineno: e.lineno,
                colno: e.colno,
            });
        });
        window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
            emit('error', 'Unhandled promise rejection', { reason: String(e.reason) });
        });
    },
};

logger.addErrorListener();
